import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import SiteHeader from '../components/SiteHeader';
import { verifyMagicLink } from '../lib/api';
import { useSession } from '../hooks/useSession';

/**
 * Consume a sign-in link.
 *
 * The token arrives in the query string, is exchanged for a session cookie, and is then
 * of no further use -- the server marks it consumed atomically, so a second attempt with
 * the same link fails even if two tabs race.
 *
 * VERIFICATION IS ATTEMPTED EXACTLY ONCE. React StrictMode double-invokes effects in
 * development, and without a guard the second invocation would consume the link the
 * first one just used, showing a spurious failure on a perfectly good sign-in.
 *
 * The token is never stored, echoed on screen, or put in application state. Every
 * failure -- unknown, expired, already used, malformed -- looks the same, because the
 * server does not distinguish them either.
 */

type VerifyState = 'verifying' | 'succeeded' | 'failed';

export default function VerifySignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [state, setState] = useState<VerifyState>('verifying');
  const attempted = useRef(false);

  const token = params.get('token');

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setState('failed');
      return;
    }

    verifyMagicLink(token)
      .then(async () => {
        await refresh();
        setState('succeeded');
        // Replace, so the token never sits in history where it could be re-visited or
        // leaked through a Referer header.
        navigate('/orders', { replace: true });
      })
      .catch(() => setState('failed'));
    // Runs once by design; the ref guards re-entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />

      <main className="mx-auto w-full max-w-lg px-5 py-14 sm:px-8 sm:py-20 flex-1 min-w-0">
        {state === 'verifying' && (
          <p className="mm-help" role="status" data-testid="verify-loading">
            Signing you in…
          </p>
        )}

        {state === 'succeeded' && (
          <p className="mm-help" role="status" data-testid="verify-success">
            You&rsquo;re signed in. Taking you to your orders…
          </p>
        )}

        {state === 'failed' && (
          <div data-testid="verify-failed">
            <h1 className="mm-display">That link didn&rsquo;t work</h1>
            <p className="mm-lede mt-4">
              Sign-in links expire quickly and can only be used once. Request a fresh one and
              it&rsquo;ll work.
            </p>
            <Link to="/sign-in" className="mm-btn-secondary mt-6 inline-flex">
              Request a new link
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
